/**
 * chatContextExtractor — derives the page-context payload sent to Ion
 * with each user message, plus the display copy shown in the welcome
 * ContextCard. Reads from globalSignals + userState + the active wouter
 * location.
 *
 * Two outputs:
 *   - getChatContextHint(location)  → payload for backend (contextHint)
 *   - getChatContextCard(location)  → {title, body, tags} for ContextCard
 *
 * Both take the location explicitly so callers can use either useLocation()
 * (reactive) or a one-shot read at submit time. They read app signals
 * (globalSignals, filterStore, and — for the assistant/assistant-home split —
 * chatMessages) but have no side effects.
 */

import { currentUser } from '../state/userState.js'
import {
  basis,
  domain,
  compareCart,
  layoutMode,
  filteredCells,
} from '../state/globalSignals.js'
import {
  selectedCathodeChemistries,
  selectedAnodeChemistries,
  selectedManufacturers,
  selectedFormFactors,
  selectedTechnologies,
} from '../state/filterStore.js'
import { getProductIdFromSlug } from '../utils/cellSlugMap.js'
// Only for the assistant / assistant-home split — see resolvePageKind below.
// No cycle: chatStore does not import this module.
import { chatMessages } from '../state/chatStore.js'

// ───────────────────────────────────────────────────────────────────────────
// Path classification
// ───────────────────────────────────────────────────────────────────────────

// Top-level routes that land users on a known page. We prefer a
// specific kind over the generic 'other' wherever possible — the
// backend's PageContext.page Literal accepts: cells, benchmarking,
// compare, cell-detail, assistant, assistant-home, admin, other.
//
// CORRECTION (2026-07-29): this comment used to say "anything not in the
// allowlist gets normalised to 'other' server-side, so the worst case of a
// stale match here is 'the LLM sees Page: other' — not a crash." That is
// FALSE. `PageContext.page` is a strict Pydantic Literal with no normaliser
// (chat/schemas.py), so an unlisted value is a 422 on the entire chat request
// — chat stops working, it does not degrade. Adding a value here means adding
// it to that Literal and deploying the BACKEND FIRST.
const ROUTE_TO_KIND = {
  '/cells': 'cells',
  '/explorer': 'cells', // legacy alias for the explorer landing page
  '/cell-selection': 'benchmarking',
  '/compare': 'compare',
  '/assistant': 'assistant',
}

function classifyLocation(pathname) {
  // Normalize trailing slash
  const path = (pathname || '/').replace(/\/$/, '') || '/'

  // Exact static-route match → map to the typed PageContext kind.
  if (ROUTE_TO_KIND[path]) return { kind: ROUTE_TO_KIND[path], path }

  // /assistant/:conversationId — still the assistant.
  //
  // Needed because the exact-match table above only knows bare `/assistant`, so
  // a conversation URL fell all the way through to `other`. (Not to
  // `cell-detail`, even though the pattern below is `/<slug>/<section>` and
  // matches this shape — that branch additionally requires the slug to resolve
  // to a real product, which "assistant" does not. Verified by removing this
  // line: the kind becomes `other`.)
  //
  // `other` is an accepted backend value, so nothing 422s — it is worse than
  // that: Ion silently loses the fact that it is on its own page. The page kind
  // is what the explorer-command tools and the view-switch rules gate on, so Ion
  // would describe a page the user is not on and refuse to steer for a reason
  // that does not apply. Caught while adding the per-conversation URLs.
  if (path.startsWith('/assistant/')) return { kind: 'assistant', path }

  // /compare/:a/:b — still a comparison view.
  if (path.startsWith('/compare/')) return { kind: 'compare', path }

  if (path.startsWith('/radar/') || path === '/radar') {
    // Radar isn't a PageContext kind in the backend allowlist; the
    // server-side fallback drops it to None — we still tag it for
    // ContextCard display purposes.
    return { kind: 'radar-section', path }
  }
  if (path.startsWith('/admin')) return { kind: 'admin', path }

  // Cell detail: /<slug>/<section>
  const cellMatch = path.match(/^\/([^/]+)\/([^/]+)$/)
  if (cellMatch && !path.startsWith('/admin')) {
    const slug = cellMatch[1]
    const section = cellMatch[2]
    const productId = getProductIdFromSlug?.(slug)
    if (productId) {
      return { kind: 'cell-detail', path, slug, section, productId }
    }
  }

  return { kind: 'other', path }
}

// ───────────────────────────────────────────────────────────────────────────
// Context hint — for backend payload
// ───────────────────────────────────────────────────────────────────────────

/**
 * Build a list of currently-visible cell ids from `filteredCells`.
 * Capped client-side at 500 to mirror the backend `PageContext.visible_cell_ids`
 * limit — sending more would just be discarded server-side.
 *
 * Returns an empty array when not on a cells-listing page (per `kind`).
 */
function extractVisibleCellIds(kind) {
  if (kind !== 'cells' && kind !== 'benchmarking' && kind !== 'compare') {
    return []
  }
  const list = filteredCells.value || []
  const ids = []
  for (let i = 0; i < list.length && ids.length < 500; i++) {
    const cell = list[i]
    // Cells expose a productId (canonical) or product_id (raw API shape).
    const id = cell?.productId || cell?.product_id
    if (typeof id === 'string' && id) ids.push(id)
  }
  return ids
}

/**
 * Build a human-readable summary of the active filter chips for the LLM.
 * Examples:
 *   "Chemistry: NMC, LFP · Manufacturer: Samsung"
 *   "Form factor: 21700 · Technology: Li-Ion"
 *   ""  — when nothing is filtered
 *
 * Kept compact (<200 chars typical) so it doesn't burn prompt tokens
 * for users with empty filters.
 */
function extractFilterSummary() {
  const parts = []
  const cath = selectedCathodeChemistries.value || []
  const anode = selectedAnodeChemistries.value || []
  // Combine cathode + anode chemistries — the user-facing UI doesn't
  // separate them visually, and the LLM doesn't need that nuance.
  const chemistries = [...cath, ...anode]
  if (chemistries.length) parts.push(`Chemistry: ${chemistries.join(', ')}`)

  const mfrs = selectedManufacturers.value || []
  if (mfrs.length) parts.push(`Manufacturer: ${mfrs.join(', ')}`)

  const formats = selectedFormFactors.value || []
  if (formats.length) parts.push(`Form factor: ${formats.join(', ')}`)

  const techs = selectedTechnologies.value || []
  if (techs.length) parts.push(`Technology: ${techs.join(', ')}`)

  return parts.join(' · ').slice(0, 500)
}

/**
 * `assistant` splits in two depending on whether a conversation exists
 * (masterplan B3). The distinction matters because the two surfaces afford
 * completely different things: mid-conversation there is a transcript (and
 * often a plot or table) to refer to, whereas on the empty landing there is
 * nothing on screen at all — and the Explorer-steering tools refuse to run
 * anywhere but `page === 'cells'`, so a filter request from the landing can
 * only be answered by OPENING the Explorer. The backend's prompt block says so
 * explicitly for `assistant-home` (system_prompt.render_page_context).
 *
 * Reads chat state from a page-context module deliberately: emptiness is not
 * derivable from the URL, and the alternative — threading a flag through every
 * caller of getChatContextHint — would put the same knowledge in more places.
 *
 * `assistant-home` must exist in the backend's PageContext.page Literal before
 * this can ship; it is a strict Literal with no normaliser, so an unknown value
 * is a 422 on the whole chat request. It was added and deployed first.
 */
function resolvePageKind(kind) {
  if (kind !== 'assistant') return kind
  return chatMessages.value.length === 0 ? 'assistant-home' : 'assistant'
}

/**
 * @param {string} location — current path (e.g. wouter useLocation()[0])
 * @returns {object} stable shape — backend `resolved_page_context()`
 *   accepts both this legacy contextHint shape AND the typed
 *   `visibleCellIds` / `filterSummary` fields. The latter map directly
 *   onto the backend's PageContext schema; the former is preserved for
 *   any older endpoint reader that hasn't migrated.
 */
export function getChatContextHint(location) {
  const cls = classifyLocation(location)
  const visibleCellIds = extractVisibleCellIds(cls.kind)
  const filterSummary = extractFilterSummary()
  return {
    page: resolvePageKind(cls.kind),
    pathname: cls.path,
    basis: basis.value,
    domain: domain.value,
    layoutMode: layoutMode.value,
    compareCart: [...(compareCart.value || [])],
    cellSlug: cls.slug || null,
    cellId: cls.productId || null,
    cellSection: cls.section || null,
    // New typed fields — backend's resolved_page_context() reads these
    // directly so the LLM sees a [PAGE CONTEXT] block with the actual
    // filtered cell list and the human-readable filter chips. Keys use
    // camelCase to match `PageContext`'s populate_by_name aliases.
    visibleCellIds,
    filterSummary: filterSummary || null,
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Context card — for the welcome state
// ───────────────────────────────────────────────────────────────────────────

const BASIS_LABELS = {
  abs: 'Absolute',
  grav: 'Gravimetric',
  vol: 'Volumetric',
}

const DOMAIN_LABELS = {
  'energy-power': 'Energy ↔ Power',
  'capacity-current': 'Capacity ↔ Current',
}

/**
 * Build the ContextCard payload shown above the welcome prompt chips.
 * Returns null when there is nothing useful to say (so the ContextCard
 * suppresses itself).
 *
 * @param {string} location
 * @returns {{title: string, body: string, tags: Array<{label, value}>} | null}
 */
export function getChatContextCard(location) {
  const cls = classifyLocation(location)
  const tags = [
    { label: 'Basis', value: BASIS_LABELS[basis.value] || basis.value },
    { label: 'Domain', value: DOMAIN_LABELS[domain.value] || domain.value },
  ]

  switch (cls.kind) {
    case 'cell-detail':
      return {
        title: 'Context',
        body: `You're viewing the ${cls.section.replace(/-/g, ' ')} section of ${cls.slug}. Ion can pull data from this cell directly.`,
        tags: [...tags, { label: 'Cell', value: cls.slug }],
      }

    case 'radar-section':
      return {
        title: 'Context',
        body: `You're on the radar section ${cls.path.replace('/radar/', '')}. Ion can summarise insights from this view.`,
        tags,
      }

    case 'compare': {
      const cart = compareCart.value || []
      if (cart.length > 0) {
        return {
          title: 'Context',
          body: `You're comparing ${cart.length} cell${cart.length === 1 ? '' : 's'}. Ion can reason across the comparison.`,
          tags: [...tags, { label: 'Compare', value: `${cart.length} cells` }],
        }
      }
      return { title: 'Context', body: `You're on the compare view.`, tags }
    }

    case 'benchmarking':
      return {
        title: 'Context',
        body: `You're benchmarking. Ion can suggest cells that match your scoring criteria.`,
        tags,
      }

    case 'cells':
      return {
        title: 'Context',
        body: `You're exploring the cell catalogue. Ion can shortlist or report on cells matching specific requirements.`,
        tags,
      }

    case 'assistant':
      return {
        title: 'Context',
        body: `You're on the Batemo Ion assistant. Ask anything about the catalogue.`,
        tags,
      }

    default:
      return null
  }
}

// ───────────────────────────────────────────────────────────────────────────
// User-name helper — first name when available, else email-prefix
// ───────────────────────────────────────────────────────────────────────────

export function getChatUserName() {
  const u = currentUser.value
  if (!u) return null
  if (u.first_name) return u.first_name
  if (u.email) return String(u.email).split('@')[0]
  return null
}
